import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import React from 'react';

export const useHistorySync = (
  historyId, 
  history, 
  loading, 
  isStreaming,
  setAnalyzedVideoId,
  setAnalyzedTitle,
  setAnalyzedUrl,
  setSelectedIdx,
  backgroundReconnect,
  userId
) => {
  const navigate = useNavigate();
  const [selectedHistoryId, setSelectedHistoryId] = useState(historyId);

  // selectedHistory 계산
  const selectedHistory = selectedHistoryId ? history.find(h => h.id === selectedHistoryId) : null;
  
  // URL에서 히스토리 ID 동기화 및 유효성 검증
  useEffect(() => {
    // historyId가 없으면 대기
    if (!historyId) return;
    
    // 백그라운드 재연결 중이거나 처리 중인 경우 검증 생략
    if (backgroundReconnect?.isReconnecting || backgroundReconnect?.backgroundProcessing) {
      console.log('🔄 [HistorySync] 백그라운드 재연결/처리 중이므로 검증 생략', {
        isReconnecting: backgroundReconnect?.isReconnecting,
        backgroundProcessing: backgroundReconnect?.backgroundProcessing
      });
      return;
    }
    
    // 🆕 직접 Firebase에서 해당 문서 확인 (history 배열에 의존하지 않음)
    const checkHistoryDirectly = async () => {
      try {
        // userId가 없으면 일단 대기 (인증 중)
        if (!userId) {
          console.log('⏰ [HistorySync] userId 대기 중...');
          return;
        }
        
        const docRef = doc(db, "users", userId, "history", historyId);
        const docSnapshot = await getDoc(docRef);
        
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          console.log('✅ [HistorySync] Firebase에서 직접 확인 - 문서 존재', {
            historyId,
            status: data.status
          });
          
          // 유효한 상태인지 확인
          const validStatuses = ['active', 'analyzing', 'stopped', 'extracting', 'processing', 'error'];
          if (validStatuses.includes(data.status)) {
            console.log('✅ [HistorySync] 유효한 히스토리 - 페이지 유지');
            return; // 유효하므로 리다이렉트하지 않음
          } else {
            console.warn('⚠️ [HistorySync] 무효한 상태', { status: data.status });
            navigate('/', { replace: true });
            return;
          }
        } else {
          console.warn('⚠️ [HistorySync] 문서가 존재하지 않음');
          navigate('/', { replace: true });
          return;
        }
      } catch (error) {
        console.error('❌ [HistorySync] Firebase 확인 중 에러:', error);
        // 에러 발생 시에는 리다이렉트하지 않고 기존 로직으로 fallback
      }
    };
    
    // history 배열이 비어있으면 직접 확인
    if (history.length === 0) {
      checkHistoryDirectly();
      return;
    }
    
    // userId가 아직 없으면 대기
    if (!userId) {
      console.log('⏰ [HistorySync] userId 설정 대기 중...');
      return;
    }
    
    // 로딩 중이거나 히스토리가 아직 없는 경우 대기
    if (loading) return;
    
    const existingHistory = history.find(h => h.id === historyId);
    
    // 히스토리가 존재하지 않거나 상태가 유효하지 않은 경우 랜딩페이지로 리다이렉트
    // 유효한 상태: 'active', 'analyzing', 'stopped' (중단된 분석 재개 가능)
    const validStatuses = ['active', 'analyzing', 'stopped', 'extracting', 'processing', 'error'];
    if (!existingHistory || !validStatuses.includes(existingHistory.status)) {
      console.warn(`⚠️ [AnalyzePage] 유효하지 않은 히스토리 접근: ${historyId}`, {
        exists: !!existingHistory,
        status: existingHistory?.status,
        validStatuses,
        totalHistories: history.length
      });
      navigate('/', { replace: true });
      return;
    }
    
    // 유효한 히스토리인 경우에만 설정
    if (selectedHistoryId !== historyId) {
      React.startTransition(() => {
        setSelectedHistoryId(historyId);
        setAnalyzedVideoId(existingHistory.videoId || '');
        setAnalyzedTitle(existingHistory.customTitle || existingHistory.title || '');
        setAnalyzedUrl(existingHistory.url || '');
        
        const idx = history.findIndex(h => h.id === historyId);
        if (idx !== -1) {
          setSelectedIdx(idx);
        }
      });
    }
  }, [historyId, history, loading, selectedHistoryId, navigate, setAnalyzedVideoId, setAnalyzedTitle, setAnalyzedUrl, setSelectedIdx, backgroundReconnect, userId]);

  // 기본 히스토리 설정 (히스토리가 있지만 선택된 것이 없을 때)
  useEffect(() => {
    if (history.length > 0 && !isStreaming) {
      if (!selectedHistoryId && !historyId) {
        React.startTransition(() => {
          setSelectedHistoryId(history[0].id);
        });
      }
    }
  }, [history, selectedHistoryId, isStreaming, historyId]);

  // 스트리밍 중일 때 첫 번째 히스토리로 설정
  useEffect(() => {
    if (isStreaming && history.length > 0 && selectedHistoryId !== history[0].id) {
      React.startTransition(() => {
        setSelectedHistoryId(history[0].id);
      });
    }
  }, [isStreaming, history, selectedHistoryId]);

  return {
    selectedHistory,
    selectedHistoryId,
    setSelectedHistoryId
  };
}; 